import { type MutationFunctionOptions, type MutationHookOptions, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type {
  createInventoryTransfer as CreateInventoryTransferData,
  CreateInventoryTransferInput,
  createInventoryTransferVariables as CreateInventoryTransferVariables,
} from 'types/graphql';
import type { CreateDoorOrigin } from '../productionRuns';

/** `/inventory-transfers` → New. */
const CREATE_INVENTORY_TRANSFER_MUTATION = gql`
  mutation createInventoryTransfer($input: CreateInventoryTransferInput!) {
    createInventoryTransfer(input: $input) {
      id
      slug
    }
  }
`;

/**
 * Header "Create new…" → Inventory Transfer. `id` joined the selection when the document left
 * CreateNewButton.tsx: every entity selection carries `id` (entity-id lint).
 */
const CREATE_INVENTORY_TRANSFER_FROM_CREATE_NEW_MUTATION = gql`
  mutation createInventoryTransferFromCreateNew($input: CreateInventoryTransferInput!) {
    createInventoryTransfer(input: $input) {
      id
      slug
    }
  }
`;

type CreateInventoryTransferOptions = Pick<
  MutationHookOptions<CreateInventoryTransferData, CreateInventoryTransferVariables>,
  'onError'
> & { origin?: CreateDoorOrigin };

export function useCreateInventoryTransfer({ origin = 'listPage', ...options }: CreateInventoryTransferOptions = {}) {
  const [mutate, { loading, error }] = useMutation<CreateInventoryTransferData, CreateInventoryTransferVariables>(
    origin === 'createNewMenu'
      ? CREATE_INVENTORY_TRANSFER_FROM_CREATE_NEW_MUTATION
      : CREATE_INVENTORY_TRANSFER_MUTATION,
    options
  );
  const create = useCallback(
    (
      input: CreateInventoryTransferInput,
      callOptions?: Pick<
        MutationFunctionOptions<CreateInventoryTransferData, CreateInventoryTransferVariables>,
        'onCompleted'
      >
    ) => mutate({ variables: { input }, ...callOptions }),
    [mutate]
  );
  return { create, loading, error };
}
